import { apiClient } from '@lib/core/api';
import type { ContentDraft } from '@lib/services/agents/contentDraftApi';

/**
 * Approval centre (#25).
 *
 * Queue-level actions are exactly three per PRD #25: **Approve / View / Discard**. Editing is not a
 * fourth action — it is a state inside the View dialog ("View 弹窗预览完整原文，支持点 Edit 切换为
 * 可编辑文本框修改后保存"), and `editApproval` backs that save.
 *
 * **A queue item IS a content draft.** Pending = a draft nobody has decided on; Processed = one that
 * has been approved/published or discarded. `id` here is the draft's id: the queue is a projection
 * over `content_drafts`, not a separate store, so the in-chat DRAFT READY card and this page are two
 * entrances to the SAME pending item — the merchant approves once, wherever they click.
 *
 * There is no Pause / Manual Takeover: PRD V1.26 removed that row, so the approval centre does not
 * intervene in a running agent at all.
 */

export type ApprovalStatus = 'pending' | 'approved' | 'discarded';
/** content_publish is the only type with an entry point this cycle. */
export type ApprovalType = 'content_publish' | 'kb_sync';

export interface Approval {
  /** The `content_drafts.id`. A queue item has no identity of its own. */
  id: string;
  agentType: string;
  type: ApprovalType;
  status: ApprovalStatus;
  contentType?: string | null;
  platform?: string | null;
  title?: string | null;
  /** The full text, so Approve can dispatch a publish without a second fetch. */
  body?: string | null;
  /** Durable media handles (raw JSON): {"items":[{"image_key","role"}]}. */
  media?: string | null;
  /**
   * PRD #25's Customer FAQ scenario: the Q&A pairs Anna's weekly sweep proposes, which the merchant
   * edits in a table before they are written to the Knowledge Base. Raw JSON as stored, shaped
   * `{"items":[{"question","answer"}]}`.
   *
   * <p>Always absent today: the upstream that produces these (#7's weekly FAQ generation) is not
   * delivered, so no `kb_sync` approval exists yet. The field and its table are here so the day it
   * lands there is nothing to design — and the table says so plainly rather than rendering an empty
   * grid that looks broken.
   */
  qaPairs?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** One proposed Q&A row inside a `kb_sync` approval. */
export interface ApprovalQaPair {
  question: string;
  answer: string;
}

/**
 * Parse the Q&A payload defensively: it crosses a service boundary as a JSON STRING, and a
 * malformed one must render as "nothing proposed", never throw inside the dialog.
 */
export function parseQaPairs(raw?: string | null): ApprovalQaPair[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { items?: unknown } | unknown[];
    const items = Array.isArray(parsed) ? parsed : (parsed?.items ?? []);
    if (!Array.isArray(items)) return [];
    return items
      .map(item => {
        const row = (item ?? {}) as Record<string, unknown>;
        return {
          question: typeof row.question === 'string' ? row.question : '',
          answer: typeof row.answer === 'string' ? row.answer : '',
        };
      })
      .filter(p => p.question || p.answer);
  } catch {
    return [];
  }
}

export interface ApprovalAudit {
  id: string;
  subjectType: string;
  /** The draft this decision was about. */
  subjectId: string;
  action: 'edited' | 'approved' | 'discarded';
  actor?: string | null;
  diffJson?: string | null;
  at?: string | null;
}

const base = (userId: string) => `/digital-staff/approvals/${encodeURIComponent(userId)}`;

/** The queue. Pending comes back OLDEST first: a queue is worked through, so the longest wait is on top. */
export async function listApprovals(
  userId: string,
  filters: { status?: ApprovalStatus; agent?: string } = {}
): Promise<Approval[]> {
  const response = await apiClient.chatbotGet<Approval[]>(
    base(userId),
    filters as Record<string, unknown>
  );
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * Record the merchant's approval — audited, and only valid while the item is still pending.
 *
 * This does NOT publish. Publishing runs through Maya's agent tools (there is no backend publish
 * channel), so the caller hands the returned item to `DraftPublishModal`, which is the same dispatch
 * the in-chat card uses. Adding a second dispatch here is how you get double posts.
 */
/**
 * `apiClient` rewrites every non-401/502 failure into a `status: 200` carrying
 * {errorCode, errorMessage}, so awaiting one of these resolves on rejection and `.catch()` never
 * runs — a rejected write is indistinguishable from a successful one at the call site.
 *
 * Every decision endpoint here shares the backend's `requirePending` guard, so they all reject the
 * same way ("Only a pending item can be edited; this one is already approved") whenever the item
 * moved underneath the merchant — a second tab, or a scheduled run that already handled it.
 */
function orThrow(data: unknown, whenSilent: string): Approval {
  const body = data as Approval & { errorCode?: number; errorMessage?: string };
  if (body?.errorCode) {
    throw new Error(body.errorMessage ?? whenSilent);
  }
  return body;
}

export async function approveApproval(userId: string, approvalId: string): Promise<Approval> {
  const response = await apiClient.chatbotPost<Approval>(
    `${base(userId)}/${encodeURIComponent(approvalId)}/approve`,
    {}
  );
  return orThrow(response.data, 'Could not record this approval');
}

export async function discardApproval(userId: string, approvalId: string): Promise<Approval> {
  const response = await apiClient.chatbotPost<Approval>(
    `${base(userId)}/${encodeURIComponent(approvalId)}/discard`,
    {}
  );
  return orThrow(response.data, 'Could not discard this item');
}

/**
 * Save an edit made inside the View dialog.
 *
 * Same shape as a draft update, because it IS one. The backend records the pre-edit text in the audit
 * trail — the draft body is overwritten in place, so that trail is the only remaining record of what
 * the agent originally wrote.
 */
export async function editApproval(
  userId: string,
  approvalId: string,
  /** `qaPairs` is the Customer FAQ scenario's edit (PRD #25); `body` is the content one. */
  edit: { title?: string; body?: string; qaPairs?: string }
): Promise<Approval> {
  const response = await apiClient.chatbotPatch<Approval>(
    `${base(userId)}/${encodeURIComponent(approvalId)}`,
    edit
  );
  // Without this the dialog treated a rejection as a successful save: the merchant was told
  // nothing and their text reverted (YEP-771).
  return orThrow(response.data, 'Could not save this edit');
}

/*
 * [Infra 1.5 #16] `getApprovalAudit()`（单条的 trail，GET …/{id}/audit）已删 —— 全仓 0 调用方，
 * 连它本该服务的 ApprovalCenterPage 都不读它。PRD #19「处理记录并入任务的操作历史，不单开
 * 审计日志页」取消了单条 trail 这个入口，这个函数是那次改动的残骸。
 *
 * 同文件其余的 approve / discard / edit / auditLog / parseQaPairs / toContentDraft **没有删**：
 * 它们还在服务 `ApprovalCenterPage`，而那一页是**有意留着**的 —— KB 问答待入库那张表要先搬进
 * 任务中心详情，页才能删（见 `router/index.tsx:83`）。那张表已随 CS PRD V1.7 移到 CS 2.0，
 * 所以这里等的是那一期，不是没人管。
 */

/** The merchant-wide audit log (the Audit Log tab). Bounded server-side. */
export async function getApprovalAuditLog(userId: string): Promise<ApprovalAudit[]> {
  const response = await apiClient.chatbotGet<ApprovalAudit[]>(`${base(userId)}/audit-log`);
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * Adapt a queue item to the `ContentDraft` shape `DraftPublishModal` takes.
 *
 * Lossless for what the modal reads (id / contentType / platform / title / body / media); the fields
 * it does not read are filled with the item's own values rather than invented ones.
 */
export function toContentDraft(approval: Approval): ContentDraft {
  return {
    id: approval.id,
    clientDraftId: null,
    conversationId: null,
    contentType: (approval.contentType ?? 'social') as ContentDraft['contentType'],
    platform: approval.platform ?? null,
    title: approval.title ?? null,
    body: approval.body ?? null,
    productsSnapshot: null,
    campaignSnapshot: null,
    media: approval.media ?? null,
    status: 'DRAFT',
    createdAt: approval.createdAt ?? null,
    updatedAt: approval.updatedAt ?? null,
  } as ContentDraft;
}
